import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeviceService } from 'src/app/services/devices/device.service';
import { getRelayDeviceImage, getRelayOutputCount } from 'src/app/services/devices/device-type.util';
import { HistoryAnalyticsDateRange } from '../history-analytics-date-filter/history-analytics-date-filter.component';

interface ChannelHistoryRecord {
  c1stat: number;
  c2stat: number;
  c1counter: number;
  c2counter: number;
  timestamp: string;
}

@Component({
  selector: 'app-history-anc-plus',
  imports: [CommonModule, TranslateModule],
  templateUrl: './history-anc-plus.component.html',
  styleUrl: './history-anc-plus.component.scss',
})
export class HistoryAncPlusComponent implements OnChanges {

  @Input() serial = '';
  @Input() deviceInfo: any = null;
  @Input() dateRange: HistoryAnalyticsDateRange | null = null;

  dataHistory: ChannelHistoryRecord[] = [];
  loadingHistory = false;
  lastUpdated: string | null = null;
  moduleInfoDevice: any;
  readonly pageSize = 10;
  currentPage = 1;

  constructor(
    private _device: DeviceService,
    private translate: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.serial || !this.dateRange) {
      return;
    }
    if (changes['serial'] || changes['dateRange']) {
      this.loadChannelHistory();
    }
  }

  private loadChannelHistory(): void {
    if (!this.serial) {
      return;
    }

    this.loadingHistory = true;
    this.dataHistory = [];

    this._device.getModuleInfo(this.serial, 'device').subscribe({
      next: (data) => {
        this.moduleInfoDevice = data;
      },
      error: () => {
        this.moduleInfoDevice = null;
      },
    });

    this._device.getModuleInfo(this.serial, 'channel', 'data').subscribe({
      next: (response) => {
        const raw = Array.isArray(response?.data) ? response.data : [];
        this.dataHistory = this.filterRecordsByDateRange(raw);
        this.currentPage = 1;
        this.lastUpdated = new Date().toISOString();
        this.loadingHistory = false;
      },
      error: () => {
        this.dataHistory = [];
        this.loadingHistory = false;
      },
    });
  }

  refreshChannelHistory(): void {
    this.loadChannelHistory();
  }

  /** Filtra en cliente hasta que la API acepte rango por query. */
  private filterRecordsByDateRange(records: ChannelHistoryRecord[]): ChannelHistoryRecord[] {
    if (!this.dateRange?.startDate || !this.dateRange?.endDate) {
      return records;
    }
    const start = new Date(this.dateRange.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(this.dateRange.endDate);
    end.setHours(23, 59, 59, 999);
    return records.filter((record) => {
      const ts = new Date(record.timestamp);
      return ts >= start && ts <= end;
    });
  }

  get deviceImage(): string {
    return getRelayDeviceImage(this.serial, this.deviceInfo?.sub_type);
  }

  get outputCount(): number {
    return getRelayOutputCount(this.serial);
  }

  get permanent(): number {
    return this.moduleInfoDevice?.cfg?.permanent ?? 0;
  }

  isOutputPermanent(output: 1 | 2): boolean {
    if (output === 1) {
      return this.permanent === 1 || this.permanent === 3;
    }
    return this.permanent === 2 || this.permanent === 3;
  }

  get deviceDisplayName(): string {
    return this.deviceInfo?.name ?? this.serial;
  }

  get deviceDisplayId(): string {
    return this.serial;
  }

  isChannelOn(stat: number): boolean {
    return stat === 2 || stat === 3;
  }

  isChannelManual(stat: number): boolean {
    return stat === 1 || stat === 3;
  }

  getChannelBulbClass(stat: number): string {
    return this.isChannelOn(stat) ? 'channel-bulb-on' : 'channel-bulb-off';
  }

  getChannelPowerClass(stat: number): string {
    return `fw-bold mb-0 ${this.isChannelOn(stat) ? 'channel-power-on' : 'channel-power-off'}`;
  }

  getChannelModeBadgeClass(stat: number): string {
    const base = 'badge rounded-pill';
    return `${base} ${this.isChannelOn(stat) ? 'channel-mode-on' : 'channel-mode-off'}`;
  }

  getStatusCardClass(stat: number): string {
    const base = 'card h-100 border-start border-4';
    return this.isChannelOn(stat)
      ? `${base} border-warning bg-warning bg-opacity-10`
      : `${base} border-secondary`;
  }

  getStatusCardStyle(stat: number): Record<string, string> | null {
    if (this.isChannelOn(stat)) {
      return {
        'border-left-color': '#e3e330',
        'background-color': 'rgba(227, 227, 48, 0.1)',
      };
    }
    return null;
  }

  getStatusPillClass(stat: number): string {
    const state = this.isChannelOn(stat) ? 'channel-status-pill--on' : 'channel-status-pill--off';
    return `channel-status-pill ${state}`;
  }

  getChannelPowerLabelKey(stat: number): string {
    return this.isChannelOn(stat) ? 'on' : 'off';
  }

  getChannelModeLabelKey(stat: number): string {
    return this.isChannelManual(stat) ? 'manualSelection' : 'automatic';
  }

  get latestChannelRecord(): ChannelHistoryRecord | null {
    return this.dataHistory.length > 0 ? this.dataHistory[0] : null;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.dataHistory.length / this.pageSize));
  }

  get paginatedHistory(): ChannelHistoryRecord[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.dataHistory.slice(start, start + this.pageSize);
  }

  get paginationFrom(): number {
    if (this.dataHistory.length === 0) {
      return 0;
    }
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get paginationTo(): number {
    return Math.min(this.currentPage * this.pageSize, this.dataHistory.length);
  }

  get paginationPages(): number[] {
    return Array.from({ length: this.totalPages }, (_, index) => index + 1);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
  }

  goToPreviousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  goToNextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  formatDateTime(dateString: string): string {
    if (!dateString) {
      return '-';
    }
    const date = new Date(dateString);
    const lang = this.translate.currentLang || this.translate.defaultLang || 'es';
    return date.toLocaleString(lang, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
